'use client';

import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Image from 'next/image';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Set up beforeinstallprompt listener as early as possible
  useEffect(() => {
    // Check if event was already captured globally
    const globalPrompt = (window as any).__pwaInstallPrompt;
    if (globalPrompt) {
      setDeferredPrompt(globalPrompt);
    }

    // Listen for custom event when prompt becomes available
    const handlePWAAvailable = () => {
      const prompt = (window as any).__pwaInstallPrompt;
      if (prompt) {
        setDeferredPrompt(prompt);
      }
    };

    window.addEventListener('pwa-install-available', handlePWAAvailable);

    // Also listen for the native event (backup)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('pwa-install-available', handlePWAAvailable);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    setIsMounted(true);

    // Clear dismissed flag (we always want to show the banner on mobile)
    localStorage.removeItem('pwa-banner-dismissed');

    // Check if running in standalone mode (PWA is installed and running)
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true || // iOS Safari
      document.referrer.includes('android-app://'); // Android TWA

    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check if mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // ONLY show banner if NOT in standalone mode (not in installed PWA app) AND on mobile
    if (standalone) {
      // PWA is installed and running - never show banner
      document.documentElement.style.setProperty('--pwa-banner-height', '0px');
      return;
    }

    if (!isMobile) {
      // Not on mobile, don't show
      document.documentElement.style.setProperty('--pwa-banner-height', '0px');
      return;
    }

    // Show banner on mobile browsers (not in PWA app). The real height is
    // measured in the layout effect below so the navbar offset always matches.
    setShowBanner(true);

    return () => {
      // Cleanup CSS variable when component unmounts
      document.documentElement.style.setProperty('--pwa-banner-height', '0px');
    };
  }, []);

  // Keep --pwa-banner-height in sync with the banner's actual rendered height
  // (it varies with safe-area inset and text wrapping), so the navbar sits
  // exactly below it with no overlap and no hidden content.
  useLayoutEffect(() => {
    if (!showBanner || isStandalone) {
      document.documentElement.style.setProperty('--pwa-banner-height', '0px');
      return;
    }
    const setVar = () => {
      const h = bannerRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty('--pwa-banner-height', `${h}px`);
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    if (bannerRef.current) ro.observe(bannerRef.current);
    window.addEventListener('resize', setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', setVar);
    };
  }, [showBanner, isStandalone]);

  // Don't render until mounted to avoid hydration mismatch
  if (!isMounted) {
    return null;
  }

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
          setDeferredPrompt(null);
          setShowBanner(false);
          // Remove CSS variable when installed
          document.documentElement.style.setProperty('--pwa-banner-height', '0px');
        }
      } catch (error) {
        // Silent fail
      }
    } else {
      // For iOS, provide specific instructions
      if (isIOS) {
        return; // iOS doesn't need a button click, instructions are already shown
      }
    }
  };

  const handleDismiss = () => {
    // Just hide the banner temporarily (will show again on page refresh)
    setShowBanner(false);
    // Remove CSS variable when banner is dismissed
    document.documentElement.style.setProperty('--pwa-banner-height', '0px');
  };

  if (!showBanner || isStandalone) {
    return null;
  }

  return (
    <>
      {/* Banner — fixed above the header (header offsets via --pwa-banner-height) */}
      <div ref={bannerRef} className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-red-950 to-red-900 border-b border-red-600/30 shadow-lg md:hidden" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="container mx-auto px-3 py-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
              <Image
                src="/image/epify-pwa.png?v=2"
                alt="Epify"
                width={36}
                height={36}
                className="w-full h-full object-cover"
                unoptimized
              />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-[11px] leading-tight">
                Get the Epify App
              </h3>
              <p className="text-gray-300 text-[9px] leading-tight">
                {isIOS
                  ? "Tap Share → Add to Home Screen"
                  : "Install for a better experience"}
              </p>
            </div>

            {!isIOS && (
              <Button
                onClick={handleInstallClick}
                size="sm"
                className="bg-white text-red-900 hover:bg-gray-100 font-semibold text-[11px] h-8 px-4 min-w-[70px]"
              >
                Install
              </Button>
            )}

            <button
              onClick={handleDismiss}
              className="flex-shrink-0 w-11 h-11 -mr-2 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4 text-gray-300" />
            </button>
          </div>
        </div>
      </div>
      {/* CSS to ensure desktop header stays at top */}
      <style jsx global>{`
        @media (min-width: 768px) {
          :root {
            --pwa-banner-height: 0px !important;
          }
        }
      `}</style>
    </>
  );
}
